import { useEffect, useState } from "react";
import { Loader2, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import ReportRowsTable from "../../components/reports/ReportRowsTable";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const FIELD_LABEL =
  "text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block";
const INPUT_CLS = "h-10 bg-white border-black/10";

export default function ScansGroupedReport({
  title,
  subtitle,
  dimension, // 'retail_outlet' | 'company' | 'city' | 'district' | 'state' | 'country'
  dropdownLabel,
  dropdownApiPath,
  dropdownPlaceholder,
}) {
  const today = todayISO();
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [valueId, setValueId] = useState("");
  const [options, setOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadOptions = async () => {
    setOptionsLoading(true);
    try {
      const { data } = await api.get(dropdownApiPath);
      setOptions(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          `Failed to load ${dropdownLabel.toLowerCase()} list`
      );
    } finally {
      setOptionsLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { dimension, from: fromDate, to: toDate };
      if (valueId) params.value_id = valueId;
      const { data } = await api.get("/reports/scans-grouped", { params });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Failed to load report"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOptions();
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimension, dropdownApiPath]);

  const onReset = () => {
    setFromDate(today);
    setToDate(today);
    setValueId("");
    setTimeout(() => load(), 0);
  };

  return (
    <div data-testid={`scan-report-${dimension}-page`}>
      <header className="bg-white border-b border-black/5 px-8 py-5 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-[#0A0A0A] tracking-tight">
            {title}
          </h1>
          {subtitle ? (
            <p className="text-xs text-[#525252] mt-0.5">{subtitle}</p>
          ) : null}
        </div>
      </header>

      <div className="p-8">
        {/* Filters */}
        <div className="bg-white border border-black/5 rounded-xl p-5 mb-5">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div>
              <Label className={FIELD_LABEL}>From Date</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className={INPUT_CLS}
                data-testid="from-date"
              />
            </div>
            <div>
              <Label className={FIELD_LABEL}>To Date</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className={INPUT_CLS}
                data-testid="to-date"
              />
            </div>
            <div>
              <Label className={FIELD_LABEL}>{dropdownLabel}</Label>
              <Select value={valueId} onValueChange={setValueId}>
                <SelectTrigger
                  className={INPUT_CLS}
                  data-testid="dimension-select"
                >
                  <SelectValue
                    placeholder={
                      dropdownPlaceholder || `All ${dropdownLabel.toLowerCase()}`
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {optionsLoading ? (
                    <div className="px-3 py-2 text-xs text-[#525252]">
                      Loading…
                    </div>
                  ) : options.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-[#525252]">
                      No {dropdownLabel.toLowerCase()} available.
                    </div>
                  ) : (
                    options.map((o) => (
                      <SelectItem key={o.id} value={o.id}>
                        {o.name || "—"}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button
                onClick={load}
                disabled={loading}
                className="bg-[#F50E02] hover:bg-[#D80C02] text-white h-10 flex-1"
                data-testid="apply-filters"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : null}
                Apply
              </Button>
              <Button
                variant="outline"
                onClick={onReset}
                className="border-black/10 h-10"
                data-testid="reset-filters"
              >
                <RefreshCcw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Results */}
        <ReportRowsTable rows={rows} loading={loading} emptyText="No scans match the selected filters." />
      </div>
    </div>
  );
}
